/**
 * P0 — Incoming Challenge Banner.
 * Detects `?challenge=<id>` in the URL and renders an "⚔️ Te retaron"
 * banner with Accept / Dismiss. Pure client-side; backed by the existing
 * local challenge ledger (`markChallengeAccepted`). No new tables, no
 * new APIs.
 */
import { useEffect, useState } from "react";
import { Swords, X, Check } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { markChallengeAccepted } from "@/lib/viralSprintV3/challengeFriend";
import { shareChallenge } from "@/lib/whatsappViral/whatsappEngine";

const SS_KEY = "vs:challenge:incoming:dismissed";

export function IncomingChallengeBanner() {
  const [id, setId] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const cid = params.get("challenge");
      if (!cid) return;
      const dismissed = sessionStorage.getItem(SS_KEY) === cid;
      if (dismissed) return;
      setId(cid);
      void trackEvent("challenge_invite_received", { id: cid });
    } catch {
      /* noop */
    }
  }, []);

  if (!id) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(SS_KEY, id); } catch { /* noop */ }
    void trackEvent("challenge_invite_dismissed", { id });
    setId(null);
  };

  const accept = () => {
    try { markChallengeAccepted(id); } catch { /* noop */ }
    void trackEvent("challenge_invite_accepted", { id });
    void trackEvent("challenge_friend_accepted", { id });
    setAccepted(true);
    window.setTimeout(() => {
      try { sessionStorage.setItem(SS_KEY, id); } catch { /* noop */ }
      setId(null);
    }, 2000);
  };

  const shareResult = () => {
    void trackEvent("challenge_result_shared", { id });
    // Route through the centralized engine so wa_share_* analytics stay consistent.
    void shareChallenge({ code: id }, { source: "incoming_challenge_banner" });
  };

  return (
    <div className="mb-3 rounded-2xl border border-[oklch(0.78_0.2_50)]/60 bg-[oklch(0.78_0.2_50)]/12 p-3 shadow-lg animate-fade-in">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[oklch(0.78_0.2_50)] text-background">
          <Swords className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-[oklch(0.78_0.2_50)]">
            ⚔️ Te retaron
          </div>
          <div className="text-sm font-black leading-tight">
            Un amigo te retó a una predicción.
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Veamos quién sabe más de fútbol — reto <span className="font-mono">#{id}</span>
          </div>
          {!accepted ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={accept}
                className="rounded-lg bg-[oklch(0.78_0.2_50)] px-3 py-1.5 text-[11px] font-black text-background transition hover:scale-[1.02] active:scale-95"
              >
                Aceptar reto
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-lg border border-border bg-background/60 px-3 py-1.5 text-[11px] font-bold text-muted-foreground hover:text-foreground"
              >
                Más tarde
              </button>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 text-[11px] font-black text-[oklch(0.7_0.18_145)]">
              <Check className="h-3.5 w-3.5" /> Reto aceptado · suerte
              <button
                type="button"
                onClick={shareResult}
                className="ml-auto rounded-md bg-[oklch(0.7_0.18_145)] px-2 py-1 text-[10px] font-black text-background"
              >
                💬 Compartir
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Cerrar"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
